<script setup lang="ts">
import { toShortAddress, useAlertsStore, useProviderConfig, watch } from '#imports';
import { EvmItemType } from '~/service/EVM/types';

const TITLE = 'Details';
const EMPTY_MESSAGE = 'Details empty';
const PLACEHOLDER = '-';
interface DetailsRowDropdownType {
    label: string;
    value: string;
    type?: 'copy' | 'address';
}

type Props =
    | {
        isLoading: true;
        initialVisibility?: boolean;
    }
    | {
        initialVisibility?: boolean;
        isLoading?: false;
        address?: AppWTFNft['address'];
        identifier?: AppWTFNft['identifier'];
        network?: AppWTFNft['network'];
        itemType?: AppWTFNft['itemType'];
    };

const props = defineProps<Props>();

const {
    map: { chainMap }
} = useProviderConfig()(['map']);

const alertStore = useAlertsStore();

const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
        alertStore.add({
            critical: false,
            description: `${text} is copied to the clipboard`,
            status: 'type.success',
            title: 'Copied'
        });
    })
        .catch((err) => {
            console.error(
                'Failed to copy text: ',
                err
            );
        });
};

const itemTypeNameMap: Record<AppWTFNft['itemType'], string> = {
    [EvmItemType.ERC1155]: 'ERC-1155',
    [EvmItemType.ERC721]: 'ERC-721',
    [EvmItemType.ERC20]: 'ERC-20',
    [EvmItemType.ERC1155_WITH_CRITERIA]: 'ERC-1155-with-criteria',
    [EvmItemType.ERC721_WITH_CRITERIA]: 'ERC-721-with-criteria',
    [EvmItemType.NATIVE]: 'Native'
} as const;

const data = ref<DetailsRowDropdownType[]>([
    {
        label: 'Contract Address',
        value: '',
        type: 'address'
    },
    {
        label: 'Token ID',
        value: '',
        type: 'copy'
    },
    {
        label: 'Token standard',
        value: ''
    },
    {
        label: 'Blockchain',
        value: ''
    },
    {
        label: 'Royalty Fee',
        value: '10%'
    }
]);

watch(() => props.isLoading, (newIsLoading) => {
    if (!newIsLoading && !props.isLoading) {
        data.value = [
            {
                label: 'Contract Address',
                value: props.address || '',
                type: 'address'
            },
            {
                label: 'Token ID',
                value: props.identifier || '',
                type: 'copy'
            },
            {
                label: 'Token standard',
                value: props.itemType ? itemTypeNameMap[props.itemType] || '' : ''
            },
            {
                label: 'Blockchain',
                value: props.network ? chainMap[props.network]?.name || '' : ''
            },
            {
                label: 'Royalty Fee',
                value: '10%'
            }
        ];
    }
}, { immediate: true });
</script>

<template>
  <TokenDropPanel :initial-visibility="initialVisibility"
                  :title="TITLE">
    <div v-if="data"
         class="display-flex flex-column gap-8">
      <div v-for="({ value, label, type }, index) in data"
           :key="index"
           class="width-full display-flex align-items-center justify-between">

        <p class="compact-400-accent text-color-secondary">
          {{ label }}
        </p>
        <p class="compact-400-numeric display-flex align-items-center gap-12"
           :class="{'text-color-brand-aviation': type === 'copy' || type === 'address'}"
        >

          <template v-if="isLoading">
            <SkeletonMain class="details_skeleton" />
          </template>
          <template v-else>
            {{ value ? (value.startsWith('0x') ? toShortAddress(value) : value) : PLACEHOLDER }}
            <button v-if="type === 'copy' || type === 'address'"
                    type="button"
                    class="btn-copy cursor-pointer"
                    aria-label="copy"
            >
              <IconMain icon="copy"
                        :size="16"
                        @click="copyToClipboard(value)"
              />
            </button>
          </template>
        </p>
      </div>
    </div>
    <div v-else
         class="display-flex align-items-center justify-center flex-column"
    >
      <IconMain icon="air"
                :size="40"
      />
      <p>{{EMPTY_MESSAGE}}</p>
    </div>
  </TokenDropPanel>
</template>
<style scoped lang="scss">
.details_skeleton {
  display: block;
  flex: 1;
  min-width: 100px;
  height: 20px;
}

.btn-copy {
  border: none;
  background: transparent;
  color: var(--bg-brand-aviation-primary);
}
</style>
